/**
 * parser.ts —— 语法分析
 *
 * 把 token 流解析成 AST。递归下降，手写。
 *
 * 文法（完整版，就这么多）：
 *
 *   program    := statement (';' statement)* ';'?
 *   statement  := IDENT call+ | IDENT '(' argList? ')'   ← 后者是语法糖
 *   call       := '.' IDENT '(' argList? ')'
 *   argList    := literal (',' literal)*
 *   literal    := STRING | NUMBER | BOOLEAN
 *
 * 关于「语法糖」那一支（`explain('文字')`）：
 *   严格来说 IL 是链式调用，explain 该写成 explain.call('文字')。
 *   但 explain 出现频率极高，而且模型特别容易把它写成普通函数调用
 *   （因为它长得就像函数）。与其让用户频繁看到「语法错误」，
 *   不如在解析层直接接受这种写法，并自动补成 explain.call(...)。
 *
 *   糖的范围被严格限制：**根对象后面直接跟括号**。只允许这一个位置出现，
 *   不会扩散到链中间（`a.b('x')('y')` 依然非法）。
 *   默认方法名由注册层提供（见 api/explain.ts 的 EXPLAIN_DEFAULT_METHOD）。
 *
 * 注意 `statement := IDENT call+` —— 一个根对象后面**必须**至少跟一次调用。
 * 光写 `setting` 是非法的：它不产生任何效果，说明 AI 没想清楚要干什么，
 * 与其静默忽略不如报错。
 *
 * 分号处理：
 *   IL 用 `;` 分隔语句。全部用分号，**不**让换行充当分隔符 ——
 *   因为模型可能把一个长参数列表换行排版，那样会误切成两条语句。
 *   不过有一个例外：末尾的分号可省（人类和模型都常忘）。
 */

import { tokenize, LexError, type Token, type Span } from './lexer';
import type { CallNode, ChainNode, LiteralValue, ProgramNode } from './ast';
import type { EvalError } from '@/types/il';

/** 语法分析失败 */
export class ParseError extends Error {
  constructor(
    message: string,
    public readonly span: Span,
  ) {
    super(message);
    this.name = 'ParseError';
  }

  toEvalError(): EvalError {
    return { kind: 'ParseError', message: this.message, pos: this.span };
  }
}

class Parser {
  private pos = 0;

  constructor(
    private readonly tokens: Token[],
    private readonly source: string,
  ) {}

  private peek(): Token {
    return this.tokens[this.pos];
  }

  private next(): Token {
    return this.tokens[this.pos++];
  }

  private expect(type: Token['type'], what: string): Token {
    const tok = this.peek();
    if (tok.type !== type) {
      throw new ParseError(
        `这里应该是${what}，但读到了「${tok.raw || '结尾'}」`,
        { start: tok.start, end: tok.end },
      );
    }
    return this.next();
  }

  /** 解析整个程序 */
  parseProgram(): ProgramNode {
    const statements: ChainNode[] = [];

    while (this.peek().type !== 'eof') {
      // 多余的分号（如 `;;`）直接跳过
      if (this.peek().type === 'semi') {
        this.next();
        continue;
      }
      statements.push(this.parseStatement());
      // 语句之间必须有分号；到结尾时可以没有
      if (this.peek().type === 'semi') this.next();
      else if (this.peek().type !== 'eof') {
        const tok = this.peek();
        throw new ParseError(
          `语句之间要用「;」分隔，「${tok.raw}」前缺了一个`,
          { start: tok.start, end: tok.end },
        );
      }
    }

    return { type: 'program', statements, source: this.source };
  }

  /** 解析一条语句：IDENT call+ ，或语法糖 IDENT( args ) */
  private parseStatement(): ChainNode {
    const rootTok = this.expect('ident', '一个对象名');
    const start = rootTok.start;

    const calls: CallNode[] = [];

    // 语法糖分支：`explain('文字')` —— 根对象后直接跟括号。
    // 补一个默认方法名的 call 节点，让 AST 的形状保持一致，
    // 求值器因此完全不需要知道有语法糖这回事。
    if (this.peek().type === 'lparen') {
      calls.push(this.parseBareCall(rootTok));
    } else {
      while (this.peek().type === 'dot') {
        calls.push(this.parseCall());
      }
    }

    if (calls.length === 0) {
      throw new ParseError(
        `「${rootTok.raw}」后面没有跟任何方法调用。要做什么？（例如 ${rootTok.raw}.xxx(...)）`,
        { start: rootTok.start, end: rootTok.end },
      );
    }

    const end = calls[calls.length - 1].end;
    return {
      type: 'chain',
      root: rootTok.raw,
      rootSpan: { start: rootTok.start, end: rootTok.end },
      calls,
      source: this.source.slice(start, end).trim(),
      start,
      end,
    };
  }

  /**
   * 解析语法糖形式的调用：IDENT ( argList? )
   * 方法名留空（由求值器换成注册层给的默认方法名）。
   */
  private parseBareCall(rootTok: Token): CallNode {
    const lparen = this.expect('lparen', '左括号「(」');
    const { args, argSpans } = this.parseArgList();
    const rparen = this.expect('rparen', '右括号「)」');

    return {
      type: 'call',
      // 空名字是语法糖的标记。求值器见到空名会换成默认方法名。
      // 这样比在解析期就去查注册表更干净 —— 解析器不该知道注册表。
      name: '',
      nameSpan: { start: rootTok.end, end: rootTok.end },
      args,
      argSpans,
      start: lparen.start,
      end: rparen.end,
    };
  }

  /** 解析实参列表（不含括号），复用给两种调用形式 */
  private parseArgList(): { args: LiteralValue[]; argSpans: Span[] } {
    const args: LiteralValue[] = [];
    const argSpans: Span[] = [];

    if (this.peek().type !== 'rparen') {
      for (;;) {
        const argTok = this.next();
        args.push(this.toLiteral(argTok));
        argSpans.push({ start: argTok.start, end: argTok.end });

        if (this.peek().type === 'comma') {
          this.next();
          continue;
        }
        break;
      }
    }

    return { args, argSpans };
  }

  /** 解析一环：. IDENT ( argList? ) */
  private parseCall(): CallNode {
    const dot = this.next(); // 已经确认是 dot
    const nameTok = this.expect('ident', '方法名');

    this.expect('lparen', `「${nameTok.raw}」后面的左括号「(」`);

    const { args, argSpans } = this.parseArgList();
    const rparen = this.expect('rparen', '右括号「)」');

    return {
      type: 'call',
      name: nameTok.raw,
      nameSpan: { start: nameTok.start, end: nameTok.end },
      args,
      argSpans,
      start: dot.start,
      end: rparen.end,
    };
  }

  /** 把 token 转成字面量。非字面量一律报错 —— IL 不支持表达式 */
  private toLiteral(tok: Token): LiteralValue {
    switch (tok.type) {
      case 'string':
      case 'number':
      case 'boolean':
        return tok.value as LiteralValue;
      case 'ident':
        // 裸标识符当参数是常见错误（应该加引号）
        throw new ParseError(
          `「${tok.raw}」如果是文字内容，要用引号包起来（如 '${tok.raw}'）`,
          { start: tok.start, end: tok.end },
        );
      default:
        throw new ParseError(
          `「${tok.raw || '结尾'}」不能作为参数`,
          { start: tok.start, end: tok.end },
        );
    }
  }
}

/**
 * 解析入口。
 *
 * 词法与语法错误统一转成 EvalError，方便上层统一处理。
 * 但为了调用方仍能区分，EvalError.kind 会标出是 LexError 还是 ParseError。
 */
export function parse(source: string): ProgramNode {
  const tokens = tokenize(source);
  return new Parser(tokens, source).parseProgram();
}

/** 解析并把任何错误转成 EvalError（不抛异常版本） */
export function tryParse(
  source: string,
): { ok: true; program: ProgramNode } | { ok: false; error: EvalError } {
  try {
    return { ok: true, program: parse(source) };
  } catch (err) {
    if (err instanceof ParseError || err instanceof LexError) {
      return { ok: false, error: err.toEvalError() };
    }
    return {
      ok: false,
      error: {
        kind: 'ParseError',
        message: err instanceof Error ? err.message : String(err),
      },
    };
  }
}
